/**
 * MIRE filter config consumed by `mapDataFilterStore` + `DataFilterPanel`.
 *
 * Each filter's `id` is the MapLibre property name on the rendered features
 * (`speed_limit`, `road_system`, `ownership`, `road_seal`); the store's
 * derived expression compares `['get', id]` to each enabled value.
 *
 * Ownership uses `displayOptions` to show a short grouped list (6 entries),
 * with the `Other` row's `expandsTo` toggling all the long-tail FHWA codes
 * together so the filter expression stays consistent with the visible UI.
 *
 * The `MireDataExplorer` overlay composes its own range filter on top of
 * `mapDataFilterStore.filter` when calling `map.setFilter`.
 */

import type { FilterConfig, FilterEnumOption } from '#lib/apps/types.js';
import { mapOwnership, mapRoadSeal, mapRoadSystem } from '#lib/common/mappings.js';

// Speed limits 20-85 in steps of 5.
const speedLimits = [20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85];

const speedLimitOptions: FilterEnumOption[] = speedLimits.map((v) => ({
	value: String(v),
	label: String(v)
}));

const roadSystemOptions: FilterEnumOption[] = Object.keys(mapRoadSystem).map((k) => ({
	value: k,
	label: mapRoadSystem[Number(k)]
}));

const roadSealOptions: FilterEnumOption[] = Object.keys(mapRoadSeal)
	.sort()
	.reverse() // 1 (Paved) first
	.map((k) => ({ value: k, label: mapRoadSeal[Number(k)] }));

// Ownership: full universe (drives the filter expression) + a short grouped
// display where 'Other' (80) toggles every code that isn't otherwise listed.
const ownershipOptions: FilterEnumOption[] = Object.keys(mapOwnership).map((k) => ({
	value: k,
	label: mapOwnership[Number(k)]
}));

const ownershipDisplayValues = ['1', '2', '3', '4', '50', '80'];
const ownershipOtherExpandsTo = ownershipOptions
	.map((o) => o.value)
	.filter((v) => !ownershipDisplayValues.includes(v));

const ownershipDisplayOptions: FilterEnumOption[] = ownershipDisplayValues.map((v) =>
	v === '80'
		? { value: v, label: mapOwnership[80], expandsTo: ownershipOtherExpandsTo }
		: { value: v, label: mapOwnership[Number(v)] }
);

export const filters: FilterConfig[] = [
	{
		id: 'speed_limit',
		label: 'Speed limit (MPH)',
		options: speedLimitOptions
	},
	{
		id: 'road_system',
		label: 'Road System',
		options: roadSystemOptions
	},
	{
		id: 'ownership',
		label: 'Ownership',
		options: ownershipOptions,
		displayOptions: ownershipDisplayOptions
	},
	{
		id: 'road_seal',
		label: 'Road surface',
		options: roadSealOptions
	}
];
